import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import useWebSocket from "@/hooks/useWebSocket";
import type {
  UniCourtProgress,
  DataMerchProgress,
} from "@/components/defaultHistory/DefaultHistory";

export interface ProgressState {
  taskType: string;
  status: string;
  message: string;
  startedAt?: string;
  elapsedSeconds?: number;
}

export interface BorrowerRealtime {
  underwritingProgress: ProgressState | null;
  unicourtProgress: UniCourtProgress | null;
  datamerchProgress: DataMerchProgress | null;
  taskError: string | null;
  isInitialProcessingFromWs: boolean;
  clearTaskError: () => void;
}

/**
 * Owns the per-borrower WebSocket subscription and translates inbound
 * messages into discrete pieces of state. Also invalidates the relevant
 * React Query caches when external systems report fresh data.
 */
export function useBorrowerRealtime(id: string | null): BorrowerRealtime {
  const queryClient = useQueryClient();

  const [underwritingProgress, setUnderwritingProgress] = useState<ProgressState | null>(null);
  const [unicourtProgress, setUnicourtProgress] = useState<UniCourtProgress | null>(null);
  const [datamerchProgress, setDatamerchProgress] = useState<DataMerchProgress | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [isInitialProcessingFromWs, setIsInitialProcessingFromWs] = useState(false);

  const { lastMessage } = useWebSocket(id ? `/borrower/${id}/` : null);

  const invalidateUnicourt = useCallback(() => {
    if (id) queryClient.invalidateQueries({ queryKey: queryKeys.unicourtCases(id) });
  }, [queryClient, id]);
  const invalidateDatamerch = useCallback(() => {
    if (id) queryClient.invalidateQueries({ queryKey: queryKeys.datamerchNotes(id) });
  }, [queryClient, id]);

  useEffect(() => {
    if (!lastMessage) return;
    const data = lastMessage;

    switch (data.type) {
      case "progress_update":
        setUnderwritingProgress({
          taskType: data.task_type,
          status: data.status,
          message: data.message,
          startedAt: data.started_at,
          elapsedSeconds: data.elapsed_seconds,
        });
        setTaskError(null);
        setIsInitialProcessingFromWs(true);
        break;

      case "task_completed":
        setUnderwritingProgress(null);
        setTaskError(null);
        setIsInitialProcessingFromWs(false);
        if (id) {
          queryClient.invalidateQueries({ queryKey: queryKeys.borrower(id) });
          queryClient.invalidateQueries({
            queryKey: ["borrowerTransactions", Number(id)] as const,
          });
        }
        break;

      case "task_error":
        setUnderwritingProgress(null);
        setTaskError(data.error);
        setIsInitialProcessingFromWs(false);
        break;

      case "underwriting_update":
        if (id) queryClient.invalidateQueries({ queryKey: queryKeys.borrower(id) });
        break;

      case "unicourt_update":
        setUnicourtProgress({
          status: data.event_type,
          message: data.data?.message,
          casesProcessed: data.data?.cases_processed,
          totalCases: data.data?.total_cases,
          caseId: data.data?.case_id,
        });
        if (
          data.event_type === "case_found" ||
          data.event_type === "case_updated" ||
          data.event_type === "completed"
        ) {
          invalidateUnicourt();
        }
        break;

      case "datamerch_update":
        setDatamerchProgress({
          status: data.event_type,
          message: data.data?.message,
          notesCount: data.data?.notes_count,
        });
        if (
          (data.event_type === "note_found" && data.data?.note) ||
          data.event_type === "completed"
        ) {
          invalidateDatamerch();
        }
        break;

      case "transaction_enrichment_completed":
        setUnderwritingProgress({
          taskType: "underwriting_analysis",
          status: "analyzing",
          message: "Transaction enrichment complete. Starting underwriting analysis...",
        });
        break;

      default:
        break;
    }
  }, [lastMessage, id, queryClient, invalidateUnicourt, invalidateDatamerch]);

  const clearTaskError = useCallback(() => setTaskError(null), []);

  return {
    underwritingProgress,
    unicourtProgress,
    datamerchProgress,
    taskError,
    isInitialProcessingFromWs,
    clearTaskError,
  };
}
